export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly path: string,
    public readonly body: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiResult<T> {
  status: number;
  body: T;
}

function parseBody(text: string): unknown {
  if (!text) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  return (await apiRequestWithMeta<T>(path, options)).body;
}

export async function apiRequestWithMeta<T>(path: string, options: RequestInit = {}): Promise<ApiResult<T>> {
  const response = await fetch(path, {
    credentials: 'include',
    ...options,
    headers: {
      Accept: 'application/json',
      ...options.headers
    }
  });

  const body = parseBody(await response.text());

  if (!response.ok) {
    throw new ApiError(`Request failed with ${response.status}`, response.status, path, body);
  }

  return {
    status: response.status,
    body: body as T
  };
}

/**
 * Prefers the API's own message over the generic status text — endpoints return
 * `{ error, errors }`, and "Request failed with 400" tells the user nothing.
 */
export function describeApiError(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.body && typeof error.body === 'object') {
    const message = (error.body as { error?: unknown }).error;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return error instanceof Error ? error.message : fallback;
}
